/**
 * Catchball Proposal Types
 * 
 * Types for catchball proposals including:
 * - Item-level value proposals
 * - Loop-level ball tosses
 * - Packet-level OST-backed proposals with readiness snapshots
 * 
 * Per canonical requirements:
 * - Catchball tracks direction, ownership, status, and artifacts (Doc 04)
 * - No invisible work; artifacts must persist and be reviewable (Doc 01)
 * - Proposals allowed pre-Arrange; readiness disclosure required (Doc 03)
 */

// ============================================================================
// Proposal Types
// ============================================================================

export type ProposalType = 'item' | 'loop' | 'packet';

export type ComparabilityState = 'pre_comparable' | 'comparable';

export type ProposalDirection = 'down' | 'up';

export type ProposedAction = 'include' | 'exclude' | 'rerank';

export type ProposedCutLine = 'above' | 'below';

// ============================================================================
// Thread Message Types
// ============================================================================

export type ThreadMessageType = 
  | 'comment'
  | 'question'
  | 'accept_provisional'
  | 'return'
  | 'reject'
  | 'clarification'
  | 'response';

export type AuthorRole = 'executive' | 'middle' | 'bottom' | 'workspace-admin' | 'system';

// ============================================================================
// Readiness Snapshot Types
// ============================================================================

/**
 * Per-item readiness state captured at proposal time.
 * Used to show executives what work remains before items are comparable.
 */
export interface ItemReadiness {
  /** Whether criteria are locked for the cycle */
  criteriaLocked: boolean;
  
  /** Per-criterion: whether the value range/scale is defined */
  rangesDefinedPerCriterion: Record<string, boolean>;
  
  /** Value coverage status for each criterion */
  valueCoverage: {
    /** Criteria with no value (proposed or accepted) */
    missingRequired: string[];
    /** Criteria with proposed but not accepted values */
    proposedOnly: string[];
    /** Criteria with accepted values */
    accepted: string[];
  };
  
  /** Whether item is at correct altitude for comparison */
  altitudeAlignment: 'in_scope' | 'out_of_scope';
  
  /** Reason for altitude mismatch (if out_of_scope) */
  altitudeReason?: string;
  
  /** Candidate pool status for this item */
  candidatePoolStatus: 'none' | 'selected' | 'locked';
}

/**
 * Complete readiness snapshot for a proposal packet.
 * Computed at proposal creation time and stored immutably.
 */
export interface ReadinessSnapshot {
  /** When the snapshot was computed */
  snapshotAt: string;
  
  /** Cycle this snapshot belongs to */
  cycleId: string;
  
  /** Per-item readiness states */
  items: Record<string, ItemReadiness>;
  
  /** Summary statistics for quick display */
  summary: {
    totalItems: number;
    itemsReady: number;
    itemsWithGaps: number;
    missingCriteriaLock: boolean;
    missingRanges: string[];
  };
}

// ============================================================================
// Structured Blockers (for Return responses)
// ============================================================================

/**
 * Structured blockers returned by executives when a proposal needs work.
 * Each blocker links to an action that resolves it.
 */
export interface StructuredBlockers {
  /** Criteria must be locked before proceeding */
  missingCriteriaLock: boolean;
  
  /** Criterion keys that need range/scale definitions */
  missingRanges: string[];
  
  /** Items missing values for specific criteria */
  missingValues: Array<{
    itemId: string;
    itemTitle?: string;
    criterionKey: string;
  }>;
  
  /** Items with altitude mismatches */
  altitudeMismatch: Array<{
    itemId: string;
    itemTitle?: string;
    reason: string;
  }>;
  
  /** Objective mismatch explanation */
  objectiveMismatch?: string;
  
  /** Free-form questions or clarifications */
  questions?: string[];
}

// ============================================================================
// Catchball Proposal
// ============================================================================

/**
 * A catchball proposal - can be item-level, loop-level, or packet-level.
 * 
 * Item-level: `item_id` populated, value/rank proposals
 * Loop-level: ball tosses between org levels
 * Packet-level: OST-backed proposals with readiness snapshots
 */
export interface CatchballProposal {
  id: string;
  cycleId: string;
  
  /** Type of proposal */
  proposalType: ProposalType;
  
  /** Associated loop (required for loop/packet types) */
  loopId?: string;
  
  /** Item being proposed on (required for item type) */
  itemId?: string;
  itemTitle?: string;

  /** The session that ranked a packet's list (`session_id`, migration 670); the call behind the list is read by it. */
  sessionId?: string;
  
  /** OST nodes included (for packet type) */
  ostNodeIds?: string[];
  
  /** Strategic objectives this packet responds to (for packet type) */
  objectiveIds?: string[];
  
  /** Readiness snapshot (for packet type) */
  readinessSnapshot?: ReadinessSnapshot;
  
  /** Comparability state (for packet type) */
  comparabilityState?: ComparabilityState;
  
  /** Parent proposal for revision chains */
  parentProposalId?: string;

  /**
   * The Ball that carried this Proposal upward.
   * Canon: one Proposal per Ball per Loop (2026-05-19 correction).
   * Nullable for proposals created before migration 440.
   */
  ballId?: string;

  // Proposer info
  proposedBy?: string;
  proposedByName?: string;
  proposedByOrgUnit?: string;
  proposedByOrgUnitName?: string;
  proposedAt: Date;
  
  // Direction
  direction: ProposalDirection;
  
  // Target
  targetOrgUnit?: string;
  targetOrgUnitName?: string;
  targetRecipients?: string[];
  
  // Item-level proposal content
  proposedRank?: number;
  proposedCutLine?: ProposedCutLine;
  proposedAction?: ProposedAction;
  
  // Rationale
  rationale: string;
  supportingEvidence?: string[];
  
  // Focus list (for loop proposals)
  focusListId?: string;

  /** Ordered ranked item ids packaged on submit (catchball_proposals.aggregated_rank_list). */
  aggregatedRankList?: string[];
  
  // Status tracking
  status: ProposalStatus;
  resolvedAt?: Date;
  resolvedBy?: string;
  resolution?: ProposalResolution;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  
  // Multi-tenancy
  workspaceId?: string;
  tenantId?: string;
}

export type ProposalStatus =
  | 'draft'
  | 'pending'
  /** Packet submitted via catchball_submit_proposal (migration 334+). */
  | 'proposed'
  | 'acknowledged'
  | 'accepted'
  | 'accepted_provisional'
  /** Returned for revision (catchball_return_for_revision, migration 763). */
  | 'returned'
  /** Legacy packet return status (catchball_return_packet, retired in 763). */
  | 'countered'
  | 'rejected'
  | 'withdrawn';

/**
 * A proposal that has been sent up and is waiting on a decision.
 *
 * `catchball_submit_proposal` writes 'proposed' for PACKET submissions
 * (migration 334, consolidated in 653) and 'pending' otherwise, so a filter
 * that checks only 'pending'/'acknowledged' silently drops every packet a team
 * actually submitted — the Mode-1 queue then reports "every ranking is
 * accepted" while a real proposal sits unseen (found in the 2026-07-27 phone
 * walkthrough, prod proposal 5f0c606a). One predicate, both surfaces.
 */
export const PROPOSAL_AWAITING_DECISION_STATUSES: ProposalStatus[] = [
  'pending',
  'proposed',
  'acknowledged',
];

export function isProposalAwaitingDecision(status: ProposalStatus): boolean {
  return PROPOSAL_AWAITING_DECISION_STATUSES.includes(status);
}

/**
 * A proposal the Evaluator accepted, outright or provisionally: what the
 * glossary's Accepted Priorities maps to on `catchball_proposals`.
 */
export const PROPOSAL_ACCEPTED_STATUSES: ProposalStatus[] = ['accepted', 'accepted_provisional'];

export type ProposalResolution = 
  | 'accepted_as_is'
  | 'accepted_with_changes'
  | 'countered'
  | 'rejected'
  | 'withdrawn';

// ============================================================================
// Catchball Thread Entry
// ============================================================================

/**
 * A thread entry for proposal discussions.
 * Can be comments, questions, or executive responses.
 */
export interface CatchballThreadEntry {
  id: string;
  
  /** The thread this belongs to (legacy grouping) */
  threadId?: string;
  
  /** The specific proposal this entry is about */
  proposalId?: string;
  
  /** Cycle context */
  cycleId: string;
  
  /** Item context (for item-level threads) */
  itemId?: string;
  
  /** Type of message */
  messageType?: ThreadMessageType;
  
  /** Role of the author */
  authorRole?: AuthorRole;
  
  /** Author info */
  authorId: string;
  authorName?: string;
  
  /** Message content */
  body: string;
  
  /** Structured blockers (for return messages) */
  structuredBlockers?: StructuredBlockers;
  
  /** Timestamps */
  createdAt: Date;
  updatedAt?: Date;
  
  // Multi-tenancy
  workspaceId?: string;
  tenantId?: string;
}

// ============================================================================
// Helper Types for UI
// ============================================================================

/**
 * Summary of a proposal for list views.
 */
export interface ProposalSummary {
  id: string;
  proposalType: ProposalType;
  proposedByName?: string;
  proposedAt: Date;
  status: ProposalStatus;
  
  // For packet type
  objectiveCount?: number;
  itemCount?: number;
  readinessSummary?: {
    ready: number;
    withGaps: number;
    total: number;
  };
  comparabilityState?: ComparabilityState;
  
  // For item type
  itemTitle?: string;
  proposedAction?: ProposedAction;
}

/**
 * Proposal with full thread history.
 */
export interface ProposalWithThread {
  proposal: CatchballProposal;
  threadEntries: CatchballThreadEntry[];
  childProposals?: CatchballProposal[]; // Revisions
}

/**
 * Objective with its proposals for inbox view.
 */
export interface ObjectiveWithProposals {
  objectiveId: string;
  objectiveName: string;
  objectiveDescription?: string;
  proposals: ProposalSummary[];
  pendingCount: number;
  returnedCount: number;
  acceptedCount: number;
}

// ============================================================================
// Create/Update Params
// ============================================================================

/**
 * Parameters for creating a proposal packet.
 */
export interface CreateProposalPacketParams {
  cycleId: string;
  loopId: string;
  objectiveIds: string[];
  ostNodeIds: string[];
  rationale: string;
  createdBy: string;
  
  /** Optional: specific item IDs if not auto-derived from OST nodes */
  itemIds?: string[];
}

/**
 * Parameters for executive response.
 */
export interface ExecutiveResponseParams {
  proposalId: string;
  responseType: 'accept_provisional' | 'return' | 'reject';
  actorId: string;
  
  /** Display name of the acting executive */
  actorName?: string;
  
  /** Required for 'return' type */
  structuredBlockers?: StructuredBlockers;
  
  /** Optional message body */
  message?: string;
}

// ============================================================================
// DB Row Mapping Helpers
// ============================================================================

/**
 * Maps database row to CatchballProposal.
 */
export function dbRowToProposal(row: Record<string, unknown>): CatchballProposal {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    proposalType: (row.proposal_type as ProposalType) || 'item',
    loopId: row.loop_id as string | undefined,
    itemId: row.item_id as string | undefined,
    itemTitle: row.item_title as string | undefined,
    ostNodeIds: row.ost_node_ids as string[] | undefined,
    objectiveIds: row.objective_ids as string[] | undefined,
    readinessSnapshot: row.readiness_snapshot as ReadinessSnapshot | undefined,
    comparabilityState: row.comparability_state as ComparabilityState | undefined,
    parentProposalId: row.parent_proposal_id as string | undefined,
    ballId: row.ball_id as string | undefined,
    proposedBy: row.proposed_by as string | undefined,
    proposedByName: row.proposed_by_name as string | undefined,
    proposedByOrgUnit: row.proposed_by_org_unit as string | undefined,
    proposedByOrgUnitName: row.proposed_by_org_unit_name as string | undefined,
    proposedAt: new Date(row.proposed_at as string),
    direction: (row.direction as ProposalDirection) || 'up',
    targetOrgUnit: row.target_org_unit as string | undefined,
    targetOrgUnitName: row.target_org_unit_name as string | undefined,
    targetRecipients: row.target_recipients as string[] | undefined,
    proposedRank: row.proposed_rank as number | undefined,
    proposedCutLine: row.proposed_cut_line as ProposedCutLine | undefined,
    proposedAction: row.proposed_action as ProposedAction | undefined,
    rationale: row.rationale as string,
    supportingEvidence: row.supporting_evidence as string[] | undefined,
    focusListId: row.focus_list_id as string | undefined,
    aggregatedRankList: (row.aggregated_rank_list as string[] | null) ?? undefined,
    status: (row.status as ProposalStatus) || 'pending',
    resolvedAt: row.resolved_at ? new Date(row.resolved_at as string) : undefined,
    resolvedBy: row.resolved_by as string | undefined,
    resolution: row.resolution as ProposalResolution | undefined,
    createdAt: new Date(row.created_at as string || row.proposed_at as string),
    updatedAt: new Date(row.updated_at as string || row.proposed_at as string),
    workspaceId: row.workspace_id as string | undefined,
    tenantId: row.tenant_id as string | undefined,
  };
}

/**
 * Maps database row to CatchballThreadEntry.
 */
export function dbRowToThreadEntry(row: Record<string, unknown>): CatchballThreadEntry {
  return {
    id: row.id as string,
    threadId: row.thread_id as string | undefined,
    proposalId: row.proposal_id as string | undefined,
    cycleId: row.cycle_id as string,
    itemId: row.item_id as string | undefined,
    messageType: row.message_type as ThreadMessageType | undefined,
    authorRole: row.author_role as AuthorRole | undefined,
    authorId: row.author_id as string,
    authorName: row.author_name as string | undefined,
    body: row.body as string || row.content as string || '',
    structuredBlockers: row.structured_blockers as StructuredBlockers | undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: row.updated_at ? new Date(row.updated_at as string) : undefined,
    workspaceId: row.workspace_id as string | undefined,
    tenantId: row.tenant_id as string | undefined,
  };
}
